export const BR_TZ = 'America/Sao_Paulo';

type DateInput = Date | string | number | null | undefined;

function toDate(v: DateInput): Date | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  if (typeof v === 'number') {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const ymdOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  if (ymdOnly) return parseLocalDate(trimmed);
  const d = new Date(trimmed);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function parseLocalDate(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) {
    throw new Error(`parseLocalDate: formato inválido "${iso}" (esperado YYYY-MM-DD).`);
  }
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

const YMD_PARTS: Intl.DateTimeFormatOptions = {
  timeZone: BR_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

function brParts(d: Date): { year: string; month: string; day: string } {
  const parts = new Intl.DateTimeFormat('en-CA', YMD_PARTS).formatToParts(d);
  const lookup = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return { year: lookup.year ?? '0000', month: lookup.month ?? '00', day: lookup.day ?? '00' };
}

export function todayLocal(): string {
  const { year, month, day } = brParts(new Date());
  return `${year}-${month}-${day}`;
}

const DEFAULT_FORMAT_OPTS: Intl.DateTimeFormatOptions = {
  timeZone: BR_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

export function formatBR(value: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (!d) return '—';
  const merged: Intl.DateTimeFormatOptions = { timeZone: BR_TZ, ...(opts ?? DEFAULT_FORMAT_OPTS) };
  if (opts && !opts.timeZone) merged.timeZone = BR_TZ;
  return new Intl.DateTimeFormat('pt-BR', merged).format(d);
}

/**
 * Formata um DIA PURO — colunas Prisma `@db.Date` (startDate, nextBillingDate,
 * dueDate, emissionDate, validUntil…). NÃO converte fuso, de propósito.
 *
 * POR QUÊ: o Prisma devolve `@db.Date` como meia-noite UTC
 * (`2026-07-15T00:00:00.000Z`). Passar isso por `formatBR` (que aplica
 * America/Sao_Paulo, UTC−3) resulta em 14/07 às 21h → a tela mostra o DIA
 * ANTERIOR. Um dia puro não tem fuso: 15/07 é 15/07 em qualquer lugar do mundo.
 *
 * Use `formatBR` pra TIMESTAMP (momento real no tempo: createdAt, issuedAt,
 * occurredAt) e `formatDayBR` pra DIA PURO. Trocar os dois erra por 1 dia.
 */
export function formatDayBR(value: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDayDate(value);
  if (!d) return '—';
  const merged: Intl.DateTimeFormatOptions = {
    ...DEFAULT_FORMAT_OPTS,
    ...(opts ?? {}),
    timeZone: 'UTC',
  };
  return new Intl.DateTimeFormat('pt-BR', merged).format(d);
}

/**
 * Normaliza um dia puro pra meia-noite UTC, lendo só a parte YYYY-MM-DD.
 *
 * Cobre as duas formas que chegam do backend:
 * - `"2026-07-15T00:00:00.000Z"` (Prisma serializa `@db.Date` assim)
 * - `"2026-07-15"` (string crua de form/query)
 *
 * Não usa `toDate` de propósito: pra string YMD pura ele aplica meia-noite
 * LOCAL, que voltaria a errar 1 dia em fusos positivos (UTC+X).
 */
function toDayDate(v: DateInput): Date | null {
  if (typeof v === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
    if (m) return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  }
  return toDate(v);
}

/**
 * YMD de um DIA PURO — colunas Prisma `@db.Date` (dueDate, discountUntil,
 * validUntil…). NÃO converte fuso, de propósito — mesmo racional do formatDayBR:
 * `@db.Date` chega como meia-noite UTC, e passar por toYMDLocal (America/
 * Sao_Paulo, UTC−3) devolve o DIA ANTERIOR.
 *
 * Use `toYMDLocal` pra TIMESTAMP (createdAt, trialEndsAt…) e `toYMDDay` pra
 * DIA PURO. Trocar os dois erra por 1 dia. (bug real 2026-08-17: o guard de
 * cobertura da suspensão por inadimplência encurtou a cobertura em 1 dia e
 * cortou um cliente pagante no último dia pago.)
 */
export function toYMDDay(value: DateInput): string {
  const d = toDayDate(value);
  if (!d) return '';
  const yr = String(d.getUTCFullYear()).padStart(4, '0');
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dy = String(d.getUTCDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
}

export function formatBRDateTime(value: DateInput): string {
  return formatBR(value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function monthKey(value: DateInput): string {
  const d = toDate(value);
  if (!d) return '0000-00';
  const { year, month } = brParts(d);
  return `${year}-${month}`;
}

export function toYMDLocal(value: DateInput): string {
  const d = toDate(value);
  if (!d) return '';
  const { year, month, day } = brParts(d);
  return `${year}-${month}-${day}`;
}

/**
 * Soma `days` (pode ser negativo) a uma data YYYY-MM-DD, preservando
 * a leitura local (sem shift de UTC). Lida com mês/ano boundary natural via
 * setDate().
 */
export function addDaysLocal(ymd: string, days: number): string {
  const d = parseLocalDate(ymd);
  d.setDate(d.getDate() + days);
  const yr = String(d.getFullYear()).padStart(4, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
}

/**
 * Soma `months` (pode ser negativo) a uma data YYYY-MM-DD, preservando a leitura
 * local (sem shift de UTC). Lida com o boundary de mês/ano via setMonth(); se o dia
 * não existir no mês de destino (ex: 31/jan -3m → fev), o JS normaliza pra frente
 * (mesma semântica de Date.setMonth) — aceitável pra cortes de elegibilidade.
 */
export function addMonthsLocal(ymd: string, months: number): string {
  const d = parseLocalDate(ymd);
  d.setMonth(d.getMonth() + months);
  const yr = String(d.getFullYear()).padStart(4, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
}

/**
 * Diferença em dias inteiros entre 2 datas YYYY-MM-DD (b - a).
 * Positivo se b > a. Útil pra "vence em N dias" / "venceu há N dias".
 *
 * Usa Date.UTC pra evitar problemas de DST.
 */
export function daysDiffLocal(a: string, b: string): number {
  const da = parseLocalDate(a);
  const db = parseLocalDate(b);
  const ms =
    Date.UTC(db.getFullYear(), db.getMonth(), db.getDate()) -
    Date.UTC(da.getFullYear(), da.getMonth(), da.getDate());
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/**
 * Formata data em texto relativo (pt-BR): "hoje", "amanhã", "ontem",
 * "vence em N dias", "venceu há N dias".
 *
 * Pra outras unidades (semanas, meses), retorna a forma absoluta DD/MM/YYYY
 * pra evitar imprecisão ("vence em 5 semanas" é confuso).
 *
 * Por padrão usa "vence em" / "venceu há". Caller pode override com
 * `{ future: 'em', past: 'há' }` pra contextos genéricos.
 */
export function formatRelativeDate(
  targetYMD: string,
  opts: { future?: string; past?: string; nowYMD?: string } = {},
): string {
  const future = opts.future ?? 'vence em';
  const past = opts.past ?? 'venceu há';
  const now = opts.nowYMD ?? todayLocal();

  const diff = daysDiffLocal(now, targetYMD);

  if (diff === 0) return 'hoje';
  if (diff === 1) return 'amanhã';
  if (diff === -1) return 'ontem';

  // Acima de 30 dias, prefere absoluto (relativo fica confuso).
  if (Math.abs(diff) > 30) return formatBR(parseLocalDate(targetYMD));

  if (diff > 0) return `${future} ${diff} dias`;
  return `${past} ${Math.abs(diff)} dias`;
}
